import { createContext, type ReactNode, useCallback, useContext, useState } from "react";

export type UpgradeReason = "tier-upgrade" | "credit-topup";

export interface UpgradeModalState {
  open: boolean;
  reason: UpgradeReason;
  message?: string;
  targetTier?: string;
}

interface UpgradeModalContextValue {
  state: UpgradeModalState;
  openUpgradeModal: (opts: { reason: UpgradeReason; message?: string; targetTier?: string; }) => void;
  closeUpgradeModal: () => void;
}

const UpgradeModalContext = createContext<UpgradeModalContextValue | null>(null);

const DEFAULT_STATE: UpgradeModalState = {
  open: false,
  reason: "tier-upgrade",
};

export function UpgradeModalProvider({ children }: { children: ReactNode; }) {
  const [state, setState] = useState<UpgradeModalState>(DEFAULT_STATE);

  const openUpgradeModal = useCallback(
    (opts: { reason: UpgradeReason; message?: string; targetTier?: string; }) => {
      setState({
        open: true,
        reason: opts.reason,
        ...(opts.message !== undefined && { message: opts.message }),
        ...(opts.targetTier !== undefined && { targetTier: opts.targetTier }),
      });
    },
    [],
  );

  const closeUpgradeModal = useCallback(() => {
    setState(DEFAULT_STATE);
  }, []);

  return (
    <UpgradeModalContext.Provider value={{ state, openUpgradeModal, closeUpgradeModal }}>
      {children}
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal() {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) {
    throw new Error("useUpgradeModal must be used within an UpgradeModalProvider");
  }
  return ctx;
}
